import { AfterViewInit, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalSpinnerService } from 'src/app/shared/services/global-spinner.service';
import { Observer } from 'rxjs';
import { cloneDeep } from 'lodash';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { Utils } from 'src/app/core/services/utils';
// ApplicationContext import removed - no longer needed after filter refactor
import { PipelineItem } from 'src/app/models/pipeline/pipeline-item.model';
import { UserRolesFilter } from 'src/app/models/pipeline/requests/user-roles-filter.model';
import { User } from 'src/app/models/user/user.model';
import { PipelineView, TpoPipelinePreferences, TpoPipelineView, WebPreferences } from 'src/app/models/web-preferences.model';
import { Constants } from 'src/app/services/constants';
import { SavePipelineViewModalComponent } from 'src/app/modules/admin-pipeline/components/save-pipeline-view-modal/save-pipeline-view-modal.component';
import { BranchService } from 'src/app/modules/admin/company/services/branch.service';
import { BranchLoanOptions } from 'src/app/modules/app-details/models/branch-loan-options.model';
import { ChannelFilterItem } from 'src/app/modules/pipeline/components/pipeline-filter/pipeline-filter.component';
import { ApplicationContextService } from 'src/app/services/application-context.service';
import { ChannelService } from 'src/app/services/channel.service';
import { CommonService } from 'src/app/services/common.service';
import { NotificationService } from 'src/app/services/notification.service';
import { PipelineService } from 'src/app/services/pipeline.service';

@Component({
    selector: 'tpo-pipeline',
    templateUrl: 'tpo-pipeline.component.html',
    styleUrls: ['tpo-pipeline.component.scss'],
    standalone: false
})
export class TpoPipelineComponent implements OnInit, AfterViewInit {

  channelsThatCanBeUsedForFiltering: ChannelFilterItem[] = null;

  pipelineData: PipelineItem[];

  filter: UserRolesFilter;

  webPreferences: WebPreferences;

  savedNamedViews: string[] = [];

  // See persistWebPreferences: one announcement per run of failures.
  private hasAnnouncedPreferenceSaveFailure: boolean = false;
  pipelineView: string = '';
  selectedStageGroup: string = 'Processing';

  // Toggles recreation of the filter child on a view switch so it re-scopes to the restored
  // stage group / statuses (the table stays bound to the current preferences the whole time).
  showFilter: boolean = true;

  protected loadingPipeline: boolean = false;

  constructor(private readonly _pipelineService: PipelineService,
    private readonly _applicationContextService: ApplicationContextService,
    private readonly _branchService: BranchService,
    private readonly _channelService: ChannelService,
    private readonly _router: Router,
    private readonly _notificationService: NotificationService,
    private readonly _spinner: GlobalSpinnerService,
    private readonly _modalService: NgbModal,
    private readonly _commonService: CommonService) {
    this.filter = new UserRolesFilter();
  }

  private getCustomFieldConfigIdFromSelectedColumn = (columnField: string): number | null => {
    if (!columnField) {
      return null;
    }

    let resolvedId: number | null = null;
    this._applicationContextService.context.subscribe(context => {
      const customDataConfig = context?.globalConfig?.customDataConfig ?? [];
      const match = customDataConfig.find((cfg: any) => {
        const cfgFieldName = (cfg?.fieldName as string | undefined) ?? '';
        const cfgColumnField = cfgFieldName
          ? cfgFieldName.charAt(0).toLowerCase() + cfgFieldName.slice(1)
          : '';
        return cfgColumnField === columnField;
      });
      // A saved column selection can still name a field an admin has since excluded from pipeline views.
      if (match?.customDataFieldConfigId != null && match.availableInPipelineAndDashboardViews !== false) {
        resolvedId = Number(match.customDataFieldConfigId);
      }
    }).unsubscribe();

    return resolvedId;
  }

  loanCount: number = 0
  upcomingClosingCount: number = 0
  lockExpiringCount: number = 0;
  totalLoanAmountUpcoming: number = 0;
  totalLoanAmountLockExpiring: number = 0;
  formattedTotalPipelineDollarValue: string;
  formattedtotalLoanAmountUpcoming: string;
  formattedtotalLoanAmountLockExpiring: string;
  loansUpcomingClosingStatsValue: string;
  loansLockExpiringStatsValue: string;

  onLoanDetailsClicked = (application: PipelineItem): void => {
    const url = this._router.createUrlTree(['/tpo/app-details/' + application.applicationId], {}).toString()
    this._router.navigateByUrl(url);
  }

  ngOnInit() {
    this._applicationContextService.context.subscribe(context => {
      const availableUsers = context.tpoUsersInCurrentCompany;
      const currentlyLoggedInTpoUser = availableUsers.find((user: User) => user.userCompanyGuid == context.currentlyLoggedInUser.userCompanyGuid);
      if (currentlyLoggedInTpoUser) {
        const externalCompanyId = currentlyLoggedInTpoUser.externalCompanyId;
        if (externalCompanyId) {
          const externalCompany = context.globalConfig.externalCompanies.find(ec => ec.externalCompanyId == externalCompanyId);
          if (externalCompany) {
            this.channelsThatCanBeUsedForFiltering = this._channelService
      .getChannelsFromCommaDelimitedString(externalCompany.enabledChannels, true)
      .map(c => ({ ...c, isChecked: false }));
          }
        }
        if (currentlyLoggedInTpoUser.branchId) {
          const observer: Observer<BranchLoanOptions> = {
            next: (options: BranchLoanOptions): void => {
              this.loadPipeline(this.filter?.isShowAsArchiveActive ?? false);
            },
            error: (err: any): void => {
              let errorMessage = "An error occurred while getting branch options.";
              if (err && err.message) {
                errorMessage = err.message;
              }
              this._notificationService.showError(errorMessage, "Error!");
            },
            complete: (): void => {
            }
          }
          this._spinner.show();
          this._commonService.getWebPreferences().subscribe(response => {
            this.webPreferences = response || new WebPreferences();

            if (!this.webPreferences.tpoPipelineFilters) {
              this.webPreferences.tpoPipelineFilters = new UserRolesFilter();
            }
            if (!this.webPreferences.pipelineReferences) {
              this.webPreferences.pipelineReferences = new PipelineView();
            }
            if (!this.webPreferences.tpoPipelinePreferences) {
              this.webPreferences.tpoPipelinePreferences = new TpoPipelinePreferences();
            }
            this.initializeTpoViews();
            this.filter = this.webPreferences.tpoPipelineFilters;
            this.channelsThatCanBeUsedForFiltering = this.channelsThatCanBeUsedForFiltering.map(c => ({ ...c, isChecked: this.filter.channels.includes(c.value) }));
            this._branchService.getExternalBranchLoanOptions(currentlyLoggedInTpoUser.branchId).subscribe(observer).add(() => {
              this._spinner.hide();
            });
          }, (err) => {
            this.webPreferences = new WebPreferences();
            this._spinner.hide();
          });
        }
      }
    })
  }

  ngAfterViewInit(): void {
    // if (this.pipelineFilterComponent) {
    //   this.pipelineFilterComponent.pipelineFilterChanged.subscribe(filter => {
    //     this.filter = filter;
    //     this.loadPipeline();
    //   })
    // }
  }

  onStageGroupChanged = (stageGroup: string) => {
    // Stage group changes trigger immediate reload via applyFiltersClicked.
    // Persist the tab as the last-used stage group so it is restored on the next reload.
    this.selectedStageGroup = stageGroup;
    if (this.webPreferences) {
      this.webPreferences.tpoPipelineSelectedStageGroup = stageGroup;
      this.refreshUnnamedViewIfActive();
      this.persistWebPreferences('stage group');
    }
  }

  onPipelineFilterChanged = (filter: UserRolesFilter) => {
    this.filter = filter;
    if (this.webPreferences) {
      this.webPreferences.tpoPipelineFilters = filter;
      this.refreshUnnamedViewIfActive();
    }
    this.loadPipeline(filter.isShowAsArchiveActive);
  }

  onColumnsNeedRefresh = () => {
    // Only refresh data - column selection is saved by the table component's debounced save
    this.refreshUnnamedViewIfActive();
    this.loadPipeline(this.filter?.isShowAsArchiveActive ?? false);
  }

  // Restore the saved default view (or the last-viewed view) into the live preferences on load.
  // If neither exists, the current (unnamed) behavior is left untouched.
  private initializeTpoViews = () => {
    // Normalize a possibly malformed/partial persisted blob before reading it, so a bad
    // savedNamedViews (null / non-array) or missing unnamedDefaultView can't throw downstream.
    let preferences = this.webPreferences.tpoPipelinePreferences;
    if (!preferences) {
      preferences = new TpoPipelinePreferences();
      this.webPreferences.tpoPipelinePreferences = preferences;
    }
    if (!Array.isArray(preferences.savedNamedViews)) {
      preferences.savedNamedViews = [];
    }
    if (!preferences.unnamedDefaultView) {
      preferences.unnamedDefaultView = new TpoPipelineView();
    }

    const savedNamedViews = preferences.savedNamedViews;
    this.savedNamedViews = savedNamedViews.map(v => v.name);

    let targetIndex = savedNamedViews.findIndex(v => v.isDefault && v.view);
    if (targetIndex < 0) {
      targetIndex = savedNamedViews.findIndex(v => v.isLastViewed && v.view);
    }

    if (targetIndex > -1) {
      savedNamedViews.forEach(v => v.isLastViewed = false);
      savedNamedViews[targetIndex].isLastViewed = true;
      this.applyViewToPreferences(savedNamedViews[targetIndex].view);
      this.pipelineView = savedNamedViews[targetIndex].name;
      this.selectedStageGroup = savedNamedViews[targetIndex].view?.selectedStageGroup || 'Processing';
      this.webPreferences.tpoPipelineSelectedStageGroup = this.selectedStageGroup;
    } else {
      this.pipelineView = '';
      this.selectedStageGroup = this.webPreferences.tpoPipelineSelectedStageGroup || 'Processing';
      // No named view is active: capture the current (unnamed) config so selecting
      // "-- Select a View --" later restores it instead of a blank factory reset.
      preferences.unnamedDefaultView = this.buildCurrentTpoView();
    }
  }

  private applyViewToPreferences = (view: TpoPipelineView) => {
    this.webPreferences.tpoPipelineFilters = cloneDeep(view?.tpoPipelineFilters) ?? new UserRolesFilter();
    this.webPreferences.pipelineReferences.pipelineSelectedColumnsForTpo = cloneDeep(view?.pipelineSelectedColumnsForTpo) ?? [];
    this.webPreferences.pipelineReferences.pipelineTableStateForTpo = cloneDeep(view?.pipelineTableStateForTpo);
  }

  // Snapshot the current filters + selected columns + table state (minus the archive toggle,
  // which is deliberately not captured so Show Archived keeps working independently of views).
  private buildCurrentTpoView = (): TpoPipelineView => {
    const view = new TpoPipelineView();
    const filterSnapshot = cloneDeep(this.filter ?? this.webPreferences.tpoPipelineFilters) ?? new UserRolesFilter();
    filterSnapshot.isShowAsArchiveActive = false;
    view.tpoPipelineFilters = filterSnapshot;
    view.pipelineSelectedColumnsForTpo = cloneDeep(this.webPreferences.pipelineReferences?.pipelineSelectedColumnsForTpo) ?? [];
    view.pipelineTableStateForTpo = cloneDeep(this.webPreferences.pipelineReferences?.pipelineTableStateForTpo);
    view.selectedStageGroup = this.selectedStageGroup || 'Processing';
    return view;
  }

  // Keep the unnamed ("-- Select a View --") snapshot in sync while no named view is active, so
  // switching to a named view and back restores the user's ad-hoc config rather than a blank reset.
  private refreshUnnamedViewIfActive = () => {
    const preferences = this.webPreferences?.tpoPipelinePreferences;
    if (!preferences || !Array.isArray(preferences.savedNamedViews)) {
      return;
    }
    const hasActiveNamedView = preferences.savedNamedViews.some(v => v.isLastViewed);
    if (!hasActiveNamedView) {
      preferences.unnamedDefaultView = this.buildCurrentTpoView();
    }
  }

  onPipelineViewChanged = () => {
    const preferences = this.webPreferences?.tpoPipelinePreferences;
    if (!preferences || !Array.isArray(preferences.savedNamedViews)) {
      return;
    }

    const currentIndex = preferences.savedNamedViews.findIndex(v => v.isLastViewed);
    if (currentIndex > -1 && preferences.savedNamedViews[currentIndex].name === this.pipelineView) {
      return;
    }

    const tempWebPreferences = cloneDeep(this.webPreferences);
    const tempPreferences = tempWebPreferences.tpoPipelinePreferences;
    tempPreferences.savedNamedViews.forEach(v => v.isLastViewed = false);

    let restoredView: TpoPipelineView;
    if (this.pipelineView) {
      const idx = tempPreferences.savedNamedViews.findIndex(v => v.name === this.pipelineView && v.view);
      if (idx > -1) {
        tempPreferences.savedNamedViews[idx].isLastViewed = true;
        restoredView = tempPreferences.savedNamedViews[idx].view;
      } else {
        restoredView = tempPreferences.unnamedDefaultView;
      }
    } else {
      restoredView = tempPreferences.unnamedDefaultView;
    }

    tempWebPreferences.tpoPipelineFilters = cloneDeep(restoredView?.tpoPipelineFilters) ?? new UserRolesFilter();
    tempWebPreferences.pipelineReferences.pipelineSelectedColumnsForTpo = cloneDeep(restoredView?.pipelineSelectedColumnsForTpo) ?? [];
    tempWebPreferences.pipelineReferences.pipelineTableStateForTpo = cloneDeep(restoredView?.pipelineTableStateForTpo);

    const restoredStageGroup = restoredView?.selectedStageGroup || 'Processing';
    tempWebPreferences.tpoPipelineSelectedStageGroup = restoredStageGroup;

    // Selecting a view exits Show Archived: saved views only ever capture the non-archive filters,
    // so applying one returns to the normal scope with the view's own loan statuses restored.
    tempWebPreferences.tpoPipelineFilters.isShowAsArchiveActive = false;

    // Assign the restored preferences synchronously so the table stays bound to the current blob and
    // its debounced / destroy-time save can't persist stale preferences (avoids a save race). Only the
    // filter child is recreated (via showFilter) so it re-scopes to the restored stage group/statuses.
    this.webPreferences = tempWebPreferences;
    this.filter = this.webPreferences.tpoPipelineFilters;
    this.selectedStageGroup = restoredStageGroup;
    this.savedNamedViews = tempPreferences.savedNamedViews.map(v => v.name);
    this.channelsThatCanBeUsedForFiltering = (this.channelsThatCanBeUsedForFiltering ?? [])
      .map(c => ({ ...c, isChecked: this.filter.channels.includes(c.value) }));
    this.persistWebPreferences('view selection');

    this.showFilter = false;
    setTimeout(() => { this.showFilter = true; });
    this.loadPipeline(false);
  }

  openSaveViewModal = () => {
    if (this.filter?.isShowAsArchiveActive) {
      // Show Archived clears the loan-status filter while active, so a snapshot taken now would
      // silently lose the view's status scope. Require leaving archive mode before saving.
      this._notificationService.showWarning('Turn off "Show Archived" before saving a pipeline view.', 'Cannot Save View');
      return;
    }
    if (!this.webPreferences.tpoPipelinePreferences) {
      this.webPreferences.tpoPipelinePreferences = new TpoPipelinePreferences();
    }
    const preferences = this.webPreferences.tpoPipelinePreferences;
    const hasActiveNamedView = preferences.savedNamedViews.some(v => v.isLastViewed);

    const modalRef = this._modalService.open(SavePipelineViewModalComponent, Constants.modalOptions.medium);
    modalRef.componentInstance.newPipelineViewName = this.pipelineView || '';
    modalRef.componentInstance.isSaveAs = !hasActiveNamedView;
    modalRef.componentInstance.isDefaultView = !hasActiveNamedView;
    modalRef.componentInstance.existingViewNames = this.savedNamedViews;

    modalRef.result.then((result: { isSaveAs: boolean; name: string }) => {
      if (!result) return;
      this.saveView(result.isSaveAs, result.name);
    }, () => { });
  }

  // Whether the named view is the current default (auto-opens on return). Drives the dropdown star.
  isDefaultView = (viewName: string): boolean => {
    const preferences = this.webPreferences?.tpoPipelinePreferences;
    if (!preferences || !Array.isArray(preferences.savedNamedViews)) {
      return false;
    }
    return preferences.savedNamedViews.some(v => v.name === viewName && v.isDefault);
  }

  // Toggle a view as the default straight from the dropdown: filled star clears it (no default →
  // fall back to unnamed/last-viewed), hollow star sets it and clears the flag on every other view.
  // Persists immediately without switching or reloading the view.
  toggleDefaultView = (viewName: string): void => {
    const preferences = this.webPreferences?.tpoPipelinePreferences;
    if (!preferences || !Array.isArray(preferences.savedNamedViews)) {
      return;
    }
    const target = preferences.savedNamedViews.find(v => v.name === viewName);
    if (!target) {
      return;
    }
    const makeDefault = !target.isDefault;
    preferences.savedNamedViews.forEach(v => v.isDefault = false);
    target.isDefault = makeDefault;
    this.persistWebPreferences('default view');
  }

  private saveView = (isSaveAs: boolean, viewName: string): void => {
    if (!this.webPreferences.tpoPipelinePreferences) {
      this.webPreferences.tpoPipelinePreferences = new TpoPipelinePreferences();
    }
    const preferences = this.webPreferences.tpoPipelinePreferences;
    const currentView = this.buildCurrentTpoView();

    if (isSaveAs) {
      preferences.savedNamedViews.forEach(v => v.isLastViewed = false);
      preferences.savedNamedViews.push({
        name: viewName,
        view: currentView,
        isLastViewed: true,
        isDefault: false
      });
    } else {
      const index = preferences.savedNamedViews.findIndex(v => v.isLastViewed);
      if (index > -1) {
        preferences.savedNamedViews[index].view = currentView;
        preferences.savedNamedViews[index].name = viewName;
      } else {
        preferences.unnamedDefaultView = currentView;
      }
    }

    this.savedNamedViews = preferences.savedNamedViews.map(v => v.name);
    this.pipelineView = viewName;
    this.persistWebPreferences('view');
  }

  deletePipelineView = (pipelineViewName: string) => {
    Swal.fire({
      title: 'Delete Pipeline View',
      text: 'Are you sure you want to delete this pipeline view?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true
    }).then((result: SweetAlertResult) => {
      if (!result.value) {
        return;
      }
      const preferences = this.webPreferences.tpoPipelinePreferences;
      const index = preferences.savedNamedViews.findIndex(v => v.name === pipelineViewName);
      if (index > -1) {
        // Only reset the live pipeline when the *active* view is deleted. Deleting a non-active
        // default view just drops it (and its default flag) without dislodging the user from the
        // view they are currently looking at.
        const wasActive = preferences.savedNamedViews[index].isLastViewed;
        preferences.savedNamedViews.splice(index, 1);
        this.savedNamedViews = preferences.savedNamedViews.map(v => v.name);
        if (wasActive) {
          this.pipelineView = '';
          this.onPipelineViewChanged();
        } else {
          this.persistWebPreferences('view deletion');
        }
      }
    });
  }

  private loadPipeline = (isShowArchived: boolean = false) => {
    if (this.webPreferences?.pipelineReferences?.pipelineSelectedColumnsForTpo) {
      const pipelineSelectedColumnsForTpo = this.webPreferences.pipelineReferences.pipelineSelectedColumnsForTpo ?? [];

      this.filter.columnsToReturn = pipelineSelectedColumnsForTpo
        ?.filter(psc => psc.dataType != 'keyDate' && psc.field && psc['isCustomField'] !== true)
        .map(sc => Utils.lowerCaseFirstLetter(sc.field)); // backward compatibility

      const keyDateSelectedColumnFields = pipelineSelectedColumnsForTpo?.filter(psc => psc.dataType == 'keyDate' && psc.field);
      this.filter.keyDatesConfigurationIdsToReturn = [];
      keyDateSelectedColumnFields?.forEach(sc => {
        if (sc.field) {
          const parts = sc.field.split('_');
          if (parts.length === 2) {
            this.filter.keyDatesConfigurationIdsToReturn.push(Number(parts[1]));
          }
        }
      });

      this.filter.customFieldConfigIdsToReturn = [];
      const customFieldSelectedColumns = pipelineSelectedColumnsForTpo
        ?.filter(psc => psc['isCustomField'] === true);
      customFieldSelectedColumns?.forEach(sc => {
        const field = sc.field as string;
        const resolvedId = this.getCustomFieldConfigIdFromSelectedColumn(field);
        if (resolvedId != null) {
          this.filter.customFieldConfigIdsToReturn.push(resolvedId);
        }
      });
    }

    this.loadingPipeline = true;
    this._pipelineService.getByFilter(this.filter, isShowArchived).subscribe((data: PipelineItem[]) => {
      this.pipelineData = data;
      this.loadingPipeline = false;
      this.calculateStatistics(this.pipelineData);
    }, err => {
      this.loadingPipeline = false;
      let errorMessage = "An error occurred while loading pipeline.";
      if (err && err.message) {
        errorMessage = err.message;
      }
      this._notificationService.showError(errorMessage, "Error!");
    });
  }

  private calculateStatistics(pipeline: PipelineItem[]) {
    this.loanCount = pipeline.length;
    this.upcomingClosingCount = 0
    this.lockExpiringCount = 0;
    var totalPipelineDollarValue: number = pipeline.reduce((prev, cur) => prev + cur.loanAmount, 0);
    this.formattedTotalPipelineDollarValue = "$" + (totalPipelineDollarValue).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

    const msToDay = 86400000;

    for (const item of pipeline) {
      const { closingDate, lockExpirationDate, loanAmount } = item;
      const amount = isNaN(loanAmount) ? 0 : loanAmount;

      if (closingDate != null && (new Date(closingDate).valueOf() >= Date.now()) && (new Date(closingDate).valueOf() <= Date.now() + 15 * msToDay)) {
        this.totalLoanAmountUpcoming += amount;
        this.upcomingClosingCount++;
      }
      if (lockExpirationDate != null && (new Date(lockExpirationDate).valueOf() >= Date.now()) && (new Date(lockExpirationDate).valueOf() <= Date.now() + 7 * msToDay)) {
        this.totalLoanAmountLockExpiring += amount;
        this.lockExpiringCount++;
      }
    }

    this.formattedtotalLoanAmountUpcoming = "$" + (this.totalLoanAmountUpcoming).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    this.formattedtotalLoanAmountLockExpiring = "$" + (this.totalLoanAmountLockExpiring).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');;
  }

  // Every preference save in this screen used to be fire-and-forget. A save that never reached the server -
  // refused client-side, or failed outright - left the dropdown showing a view that was only ever in memory,
  // so it looked saved until the next reload took it away with no error anywhere (LLS-288). Say so instead.
  //
  // Only the first failure in a run is announced. Once saving is broken it stays broken until the page is
  // reloaded, and the stage-group tabs save on every click, so repeating the toast would bury the screen without
  // telling the user anything new. A save that succeeds re-arms it.
  private persistWebPreferences = (whatWasBeingSaved: string): void => {
    this._commonService.saveWebPreferences(this.webPreferences).subscribe({
      next: () => this.hasAnnouncedPreferenceSaveFailure = false,
      error: () => {
        if (this.hasAnnouncedPreferenceSaveFailure) {
          return;
        }

        this.hasAnnouncedPreferenceSaveFailure = true;
        // Deliberately not "try again": a refused save repeats until the page is reloaded under a consistent
        // session, and the screen is still showing the change that did not persist.
        this._notificationService.showError(
          `Your ${whatWasBeingSaved} was not saved. This page is out of date - reload it before making further changes.`,
          'Not Saved');
      }
    });
  }
}
